import { api } from './api';
import { sessionGeneration } from './state';

// #3805: each active Goal's customers (the domains of the people its matter is
// with, inferred by the server, #3787) and its 案件 (parent Goal, migration-234),
// read after the list is drawn so the list is not slowed. A failed read leaves
// the list as it is, without them.

export interface GoalCustomer {
  domain: string;
  link: 'inferred_by_address';
  /** The subdomains the server gathered into `domain` by inference. */
  merged_domains?: string[];
}

/** The 案件 a Goal is under (Yoshie 2026-11-03). */
export interface GoalProject {
  goal_id: string;
  title: string;
}

let customersByGoal = new Map<string, GoalCustomer[]>();
let projectByGoal = new Map<string, GoalProject>();
let reading: Promise<void> | null = null;
const readers = new Set<() => void>();

/** Draws `draw` again whenever the customers arrive (the list, the search results). */
export function onGoalCustomersRead(draw: () => void) {
  readers.add(draw);
}

/** The customers of one Goal; empty while not read or when it has none. */
export function goalCustomers(goalId: string): GoalCustomer[] {
  return customersByGoal.get(goalId) ?? [];
}

/** The 案件 the Goal is under; null while not read or when it is under none. */
export function goalProject(goalId: string): GoalProject | null {
  return projectByGoal.get(goalId) ?? null;
}

/** Whether the Goal is with this customer domain. */
export function goalHasCustomer(goalId: string, domain: string): boolean {
  return goalCustomers(goalId).some((customer) => customer.domain === domain);
}

/** The customer domains whose name holds the query, with how many Goals each has. */
export function customersMatching(query: string): Array<{ domain: string; count: number }> {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const counts = new Map<string, number>();
  for (const customers of customersByGoal.values()) {
    for (const { domain } of customers) {
      if (domain.includes(q)) counts.set(domain, (counts.get(domain) ?? 0) + 1);
    }
  }
  return [...counts].map(([domain, count]) => ({ domain, count }))
    .sort((a, b) => b.count - a.count || a.domain.localeCompare(b.domain));
}

/** Test seam and sign-out: replaces what was read. */
export function setGoalCustomers(entries: Record<string, GoalCustomer[]>, projects: Record<string, GoalProject> = {}) {
  customersByGoal = new Map(Object.entries(entries));
  projectByGoal = new Map(Object.entries(projects));
}

export function resetGoalCustomers() {
  customersByGoal = new Map();
  projectByGoal = new Map();
}

/** Reads the customers, then draws what showed them again. Reads already running are shared. */
export function loadGoalCustomers(): Promise<void> {
  if (reading) return reading;
  const generation = sessionGeneration;
  reading = (async () => {
    try {
      const data = await api('/api/goals/groupings') as { customers?: Record<string, GoalCustomer[]>; projects?: Record<string, GoalProject> };
      if (generation !== sessionGeneration) return;
      setGoalCustomers(data.customers ?? {}, data.projects ?? {});
      for (const draw of readers) draw();
    } catch {
      // The list stays without customers.
    }
  })().finally(() => { reading = null; });
  return reading;
}
